"use client";

import Link from "next/link";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";

/**
 * CategoryHubTemplate — reusable scaffold for top-level category pages.
 * Mirrors the premium patterns from the homepage:
 *  - Hero with eyebrow, gradient title, lede, dual CTAs
 *  - 5-stat stats bar
 *  - Bento grid for "what's inside"
 *  - Feature matrix for individual items
 *  - Use cases segment grid
 *  - Why-choose differentiator cards
 *  - FAQ + PAA accordions
 *  - End-cap CTA with trust bar
 *
 * All sections accept typed data + children for full customisation per category.
 */

export interface HubHeroStat {
  value: string;
  label: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export interface HubHeroCTA {
  label: string;
  href: string;
}

export interface HubHeroProps {
  eyebrow: string;
  title: string;
  titleAccent: string;
  lede: string;
  ctaPrimary: HubHeroCTA;
  ctaSecondary?: HubHeroCTA;
  stats?: HubHeroStat[];
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet";
}

export function HubHero({ eyebrow, title, titleAccent, lede, ctaPrimary, ctaSecondary, stats, accent }: HubHeroProps) {
  const accentVar = `var(--color-${accent})`;
  return (
    <section id="top" className="relative overflow-hidden pt-12 pb-16 sm:pt-16 sm:pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full opacity-25 blur-[140px] animate-pulse-glow"
          style={{ background: accentVar }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 h-72 w-72 rounded-full bg-[var(--color-purple)] opacity-25 blur-[120px] animate-pulse-glow"
          style={{ animationDelay: "2s" }}
        />
      </div>
      <Container>
        <Reveal>
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-6">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: accentVar }} />
              <span className="eyebrow-tiny" style={{ color: accentVar }}>{eyebrow}</span>
            </div>
          </div>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="display-hero text-balance">
            {title}{" "}
            <span className="bg-gradient-to-br from-[var(--color-crimson)] via-[var(--color-purple)] to-[var(--color-cyan)] bg-clip-text text-transparent">
              {titleAccent}
            </span>
          </h1>
        </Reveal>
        <Reveal delay={160}>
          <p className="mt-6 max-w-3xl text-body leading-relaxed text-[rgb(var(--fg-muted))]">{lede}</p>
        </Reveal>
        <Reveal delay={220}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              href={ctaPrimary.href}
              className="btn-primary-gradient h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl"
              style={{ boxShadow: `0 25px 50px -12px ${accentVar}40` }}
            >
              {ctaPrimary.label} <ArrowRight className="h-4 w-4" />
            </Link>
            {ctaSecondary && (
              <Link
                href={ctaSecondary.href}
                className="btn-glass h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base"
              >
                {ctaSecondary.label}
              </Link>
            )}
          </div>
        </Reveal>
        {stats && stats.length > 0 && (
          <Reveal delay={300}>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-5xl">
              {stats.map((s) => {
                const accentMap = ["crimson", "purple", "cyan", "emerald", "amber"] as const;
                const a = accentMap[stats.indexOf(s) % accentMap.length]!;
                return (
                  <div key={s.label} className="glass rounded-2xl p-4 flex items-center gap-3">
                    <span
                      className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                      style={{
                        background: `color-mix(in oklch, var(--color-${a}) 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${a}) 35%, transparent)`,
                      }}
                    >
                      <span className="text-xs font-bold" style={{ color: `var(--color-${a})` }}>★</span>
                    </span>
                    <div>
                      <div className="text-lg font-bold" style={{ color: `var(--color-${a})` }}>{s.value}</div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-[rgb(var(--fg-subtle))] leading-tight">{s.label}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        )}
      </Container>
    </section>
  );
}

export interface HubOverviewItem {
  code: string;
  title: string;
  description: string;
  href: string;
  icon?: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export interface HubOverviewProps {
  eyebrow: string;
  title: string;
  description?: string;
  items: HubOverviewItem[];
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export function HubOverview({ eyebrow, title, description, items, accent }: HubOverviewProps) {
  const accentVar = `var(--color-${accent})`;
  return (
    <Section id="overview" className="relative">
      <Container>
        <SectionHeading eyebrow={eyebrow} title={title} description={description} accent={accent} />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item, i) => (
            <Reveal key={item.href} delay={i * 60}>
              <Link
                href={item.href}
                className="group block glass rounded-2xl p-6 h-full hover:scale-[1.02] transition-transform"
                style={{
                  boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${item.accent}) 12%, transparent)`,
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <span
                    className="grid place-items-center h-10 w-10 rounded-lg"
                    style={{
                      background: `color-mix(in oklch, var(--color-${item.accent}) 18%, transparent)`,
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${item.accent}) 35%, transparent)`,
                    }}
                  >
                    <span className="text-[10px] font-bold tracking-wider" style={{ color: `var(--color-${item.accent})` }}>{item.code}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-[rgb(var(--fg-subtle))] group-hover:translate-x-1 transition-transform" />
                </div>
                <h3 className="text-base">{item.title}</h3>
                <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{item.description}</p>
                <div className="mt-4 text-xs font-semibold" style={{ color: accentVar }}>
                  Explore →
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}

export interface HubFeatureItem {
  title: string;
  description: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export interface HubFeaturesProps {
  eyebrow: string;
  title: string;
  description?: string;
  features: HubFeatureItem[];
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export function HubFeatures({ eyebrow, title, description, features, accent }: HubFeaturesProps) {
  return (
    <Section id="features" className="relative">
      <Container>
        <SectionHeading eyebrow={eyebrow} title={title} description={description} accent={accent} />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f, i) => (
            <Reveal key={f.title} delay={i * 50}>
              <div
                className="glass rounded-2xl p-5 h-full"
                style={{
                  boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${f.accent}) 12%, transparent)`,
                }}
              >
                <span
                  className="grid place-items-center h-10 w-10 rounded-lg"
                  style={{
                    background: `color-mix(in oklch, var(--color-${f.accent}) 18%, transparent)`,
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${f.accent}) 35%, transparent)`,
                  }}
                >
                  <span className="text-base font-bold" style={{ color: `var(--color-${f.accent})` }}>◆</span>
                </span>
                <h3 className="mt-3 text-base">{f.title}</h3>
                <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{f.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}

export interface HubUseCaseItem {
  title: string;
  body: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export interface HubUseCasesProps {
  eyebrow: string;
  title: string;
  description?: string;
  cases: HubUseCaseItem[];
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export function HubUseCases({ eyebrow, title, description, cases, accent }: HubUseCasesProps) {
  return (
    <Section id="use-cases" className="relative">
      <Container>
        <SectionHeading eyebrow={eyebrow} title={title} description={description} accent={accent} />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cases.map((c, i) => (
            <Reveal key={c.title} delay={i * 60}>
              <div
                className="glass rounded-2xl p-6 h-full"
                style={{
                  boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${c.accent}) 12%, transparent)`,
                }}
              >
                <span
                  className="grid place-items-center h-11 w-11 rounded-xl"
                  style={{
                    background: `color-mix(in oklch, var(--color-${c.accent}) 18%, transparent)`,
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${c.accent}) 35%, transparent)`,
                  }}
                >
                  <span className="text-lg font-bold" style={{ color: `var(--color-${c.accent})` }}>◉</span>
                </span>
                <h3 className="mt-4 text-lg">{c.title}</h3>
                <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}

export interface HubWhyItem {
  title: string;
  body: string;
  metric?: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export interface HubWhyProps {
  eyebrow: string;
  title: string;
  items: HubWhyItem[];
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export function HubWhy({ eyebrow, title, items, accent }: HubWhyProps) {
  return (
    <Section id="why" className="relative">
      <Container>
        <SectionHeading eyebrow={eyebrow} title={title} accent={accent} />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item, i) => {
            const a = item.accent ?? accents[i % accents.length]!;
            return (
              <Reveal key={item.title} delay={i * 60}>
                <div
                  className="glass rounded-2xl p-6 h-full"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${a} 12%, transparent)`,
                  }}
                >
                  <div className="flex items-baseline gap-2">
                    {item.metric && (
                      <span className="text-3xl font-bold" style={{ color: a }}>{item.metric}</span>
                    )}
                  </div>
                  <h3 className="mt-3 text-lg">{item.title}</h3>
                  <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{item.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}

export interface HubFaqItem { q: string; a: string }
export interface HubFAQProps {
  eyebrow: string;
  title: string;
  items: HubFaqItem[];
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export function HubFAQ({ eyebrow, title, items, accent }: HubFAQProps) {
  const accentVar = `var(--color-${accent})`;
  return (
    <Section id="faq" className="relative">
      <Container>
        <SectionHeading eyebrow={eyebrow} title={title} accent={accent} />
        <div className="mt-10 max-w-3xl mx-auto space-y-3">
          {items.map((f, i) => (
            <Reveal key={f.q} delay={i * 40}>
              <details
                className="glass rounded-2xl group"
                style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accentVar} 12%, transparent)` }}
              >
                <summary className="flex items-center justify-between gap-4 px-5 py-4 cursor-pointer list-none hover:bg-[rgb(var(--border)/0.04)] transition-colors rounded-2xl">
                  <span className="font-semibold text-sm text-left">{f.q}</span>
                  <span className="text-lg shrink-0 transition-transform group-open:rotate-45" style={{ color: accentVar }}>+</span>
                </summary>
                <div className="px-5 pb-5 -mt-1 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{f.a}</div>
              </details>
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}

export interface HubCTAProps {
  eyebrow: string;
  title: string;
  description: string;
  primary: HubHeroCTA;
  secondary?: HubHeroCTA;
  trustItems?: string[];
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export function HubCTA({ eyebrow, title, description, primary, secondary, trustItems, accent }: HubCTAProps) {
  const accentVar = `var(--color-${accent})`;
  return (
    <Section id="cta" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full opacity-25 blur-[160px] animate-pulse-glow"
          style={{ background: accentVar }}
        />
      </div>
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: accentVar }}>{eyebrow}</span>
            <h2 className="mt-3">{title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{description}</p>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              href={primary.href}
              className="btn-primary-gradient h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl"
              style={{ boxShadow: `0 25px 50px -12px ${accentVar}40` }}
            >
              {primary.label} <ArrowRight className="h-4 w-4" />
            </Link>
            {secondary && (
              <Link
                href={secondary.href}
                className="btn-glass h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base"
              >
                {secondary.label}
              </Link>
            )}
          </div>
        </Reveal>
        {trustItems && trustItems.length > 0 && (
          <Reveal delay={200}>
            <div className="mt-10 glass rounded-2xl p-5">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {trustItems.map((item, i) => {
                  const a = ["crimson", "amber", "purple", "cyan", "emerald"][i % 5]!;
                  return (
                    <div key={item} className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: `var(--color-${a})` }} />
                      <span className="text-xs font-semibold leading-tight">{item}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        )}
      </Container>
    </Section>
  );
}

const accents = [
  "var(--color-crimson)",
  "var(--color-purple)",
  "var(--color-cyan)",
  "var(--color-emerald)",
  "var(--color-amber)",
  "var(--color-denim)",
  "var(--color-violet)",
] as const;

/**
 * Composes the full hub page in alternating aurora-wrapped sections.
 */
export interface CategoryHubComposerProps {
  hero: React.ReactNode;
  overview?: React.ReactNode;
  features?: React.ReactNode;
  useCases?: React.ReactNode;
  why?: React.ReactNode;
  faq?: React.ReactNode;
  cta?: React.ReactNode;
  overviewWrap?: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
  featuresWrap?: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
  useCasesWrap?: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
  whyWrap?: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
  faqWrap?: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
  ctaWrap?: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export function CategoryHubComposer({
  hero,
  overview,
  features,
  useCases,
  why,
  faq,
  cta,
  overviewWrap,
  featuresWrap,
  useCasesWrap,
  whyWrap,
  faqWrap,
  ctaWrap,
}: CategoryHubComposerProps) {
  return (
    <>
      {hero}
      {overview && overviewWrap && <div className={`bg-aurora-${overviewWrap}`}>{overview}</div>}
      {overview && !overviewWrap && overview}
      {features && featuresWrap && <div className={`bg-aurora-${featuresWrap}`}>{features}</div>}
      {features && !featuresWrap && features}
      {useCases && useCasesWrap && <div className={`bg-aurora-${useCasesWrap}`}>{useCases}</div>}
      {useCases && !useCasesWrap && useCases}
      {why && whyWrap && <div className={`bg-aurora-${whyWrap}`}>{why}</div>}
      {why && !whyWrap && why}
      {faq && faqWrap && <div className={`bg-aurora-${faqWrap}`}>{faq}</div>}
      {faq && !faqWrap && faq}
      {cta && ctaWrap && <div className={`bg-aurora-${ctaWrap}`}>{cta}</div>}
      {cta && !ctaWrap && cta}
    </>
  );
}